import {
  parseCurrentQueryString,
  getCookie,
  setCookie,
  deleteExistingBpIdUberIdCookies,
  navigateToExternal,
  isEmpty,
  deleteExistingDelegateResetCookies
} from '../../core/util';
import { DataLoader } from '../../core/types/data-loader';
import { StateStore } from '../../core/types/application-state';
import config from '../../config';
import {
  convertFONASUProspectCustomer,
  getCustomerDataById
} from '../shared/services/customer-service';
import { commitFONASUCredentials } from '../front-office/shared/loaders';
import {
  fetchAndfilterAppsForDelegateUser,
  isUserNamePresent,
  pushToBrokerageDelegateUser
} from './utils';
import { getAllApplicationsByEmail } from '../shared/services/profile-service';
import { delegateCookieDelete } from '../shared/services/user-service';
import { getBPId } from '../shared/services/business-service';

export const convertUberToBpId = async (): Promise<any> => {
  const currDate = new Date();
  const currTime = currDate.getTime();
  const bpIdExpireTime = currTime + 60 * 60 * 1000;
  currDate.setTime(bpIdExpireTime);
  const uberId = getCookie('uberId');

  let { businessPartyID } = await convertFONASUProspectCustomer(uberId);

  if (isEmpty(businessPartyID) || businessPartyID === 0) {
    const { businessParty } = await getBPId(uberId);
    businessPartyID = businessParty;
  }

  setCookie('bpId', businessPartyID, currDate.toUTCString(), true);
  return businessPartyID;
};

/* eslint-disable complexity */
export const loadBPId: DataLoader = async (): Promise<StateStore> => {
  const qs = parseCurrentQueryString();
  const { uberId, bpId } = qs || {};
  // set bpId cookie expiry time to 60 mins
  const currDate = new Date();
  const currTime = currDate.getTime();
  const bpIdExpireTime = currTime + 60 * 60 * 1000;
  currDate.setTime(bpIdExpireTime);

  // Nov Hotfix: call service to delete delegated user cookie
  try {
    await delegateCookieDelete();
  } catch (err) {
    /* eslint-disable no-console */
    console.error('Delegate Fail', err?.message);
  }

  // set the bpId cookie once this screen is launched from salesforce
  if (bpId) {
    deleteExistingBpIdUberIdCookies();
    setCookie('bpId', bpId, currDate.toUTCString(), true);
  }
  // DE67118 - if uberId is passed instead of bpId, set as cookie for conversion on AF continuation
  // US551876 - bpId conversion endpoint to be trigger after oauth token is set to session
  if (uberId && !bpId) {
    deleteExistingBpIdUberIdCookies();
    setCookie('uberId', uberId, currDate.toUTCString(), true);
  }

  return {
    app: {}
  };
};

export const loadCustomer: DataLoader = async (): Promise<StateStore> => {
  // get the bpId cookie value
  let bpIdValue = getCookie('bpId');

  // DE67118 - convert uberId to bpId; necessary to get customer data
  if (isEmpty(bpIdValue)) {
    bpIdValue = await convertUberToBpId();
  }

  const customerData = await getCustomerDataById(Number(bpIdValue));
  return {
    app: {},
    loadedData: {
      customerId: customerData?.username,
      customerFirstName: customerData?.firstName,
      customerLastName: customerData?.lastName,
      customerPrimaryEmail: customerData?.primaryEmailAddress
    }
  };
};

export const commitBRKFONASUUserName = async ({
  store,
  inputs
}): Promise<any> => {
  const { loadedData = {} } = store;
  const tempPwdCreationResults = await commitFONASUCredentials(store, inputs);

  // On Temp password creation success route to BRK flow
  if (tempPwdCreationResults?.loadedData?.tempPassword) {
    // delete delegateReset cookie by default
    deleteExistingDelegateResetCookies();
    // set the cookie expiry time to 60 mins
    const currDate = new Date();
    const currTime = currDate.getTime();
    const delegateResetExpireTime = currTime + 60 * 60 * 1000;
    currDate.setTime(delegateResetExpireTime);
    // set delegateReset cookie to true before pushing user to BRK flow
    setCookie('delegateReset', true, currDate.toUTCString(), true);

    return pushToBrokerageDelegateUser(
      loadedData?.brkAccType,
      tempPwdCreationResults?.loadedData?.loginID
    );
  }

  return {
    ...tempPwdCreationResults
  };
};

export const commitBrokerageAccountSelection: DataLoader = async ({
  store,
  chosen
}): Promise<any> => {
  const { loadedData = {} } = store;
  if (isUserNamePresent(loadedData)) {
    const allReturnJourneyAppsByEmail = await getAllApplicationsByEmail(
      loadedData?.customerPrimaryEmail
    );

    // check return journey profiles to see if any match current account selection
    if (allReturnJourneyAppsByEmail.length) {
      return fetchAndfilterAppsForDelegateUser(
        chosen,
        loadedData,
        allReturnJourneyAppsByEmail
      );
    }

    return pushToBrokerageDelegateUser(chosen, loadedData?.customerId);
  }
  return {
    loadedData: {
      brkAccType: chosen
    }
  };
};

export const routeUserToInternalAccFinder: DataLoader = async ({
  chosen
}): Promise<any> => {
  // MF Internal Account Finder Routing
  if (chosen === 'mfAccountType') {
    const mfInternalAccFinderUrl = `${config(
      'internalReturnUrlPrefix'
    )}personal-investing/tools/nasu/front-office/account-finder/mutual-funds`;

    return navigateToExternal(mfInternalAccFinderUrl);
  }

  // BRK Internal Account Finder Routing with FederatedToken
  const ADFSUrlWithFederatedToken = config('internalADFSFederationURL');

  const brkInternalAccFinderUrl = `${ADFSUrlWithFederatedToken}${config(
    'internalReturnUrlPrefix'
  )}personal-investing/tools/nasu/front-office/account-finder/brokerage`;

  return navigateToExternal(brkInternalAccFinderUrl);
};
